import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { Capability, EmployeeProfile } from '@/types'
import { Award, Play, Zap } from 'lucide-react'

interface CapabilityShowcaseProps {
  employee: EmployeeProfile
}

/**
 * Capability Showcase Component - Modern Design
 * 
 * Clean, modern component displaying employee abilities without card containers:
 * - Core Capabilities with clean layout
 * - Demo Videos with modern styling
 * - Technical Specifications with minimalist design
 */
export function CapabilityShowcase({ employee }: CapabilityShowcaseProps) {

  const getLevelDot = (level: Capability['level']) => {
    const baseClasses = "w-2 h-2 rounded-full"
    switch (level) {
      case 'beginner':
        return `${baseClasses} bg-amber-400`
      case 'intermediate':
        return `${baseClasses} bg-blue-400`
      case 'advanced':
        return `${baseClasses} bg-purple-400`
      case 'expert':
        return `${baseClasses} bg-emerald-400`
      default:
        return `${baseClasses} bg-gray-400`
    }
  }

  return (
    <div className="space-y-8">
      {/* Core Capabilities */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Core Capabilities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {employee.capabilities.map((capability) => {
              // Define specific border classes for certain capabilities
              const getBorderClasses = (name: string) => {
                const lowerName = name.toLowerCase()
                if (lowerName.includes('multilingual')) {
                  return 'border-r border-b border-border/30 pr-4 pb-4'
                }
                if (lowerName.includes('sentiment') || lowerName.includes('sentimental')) {
                  return 'border-b border-border/30 pb-4'
                }
                if (lowerName.includes('ticker') && lowerName.includes('management')) {
                  return 'border-r border-border/30 pr-4'
                }
                return ''
              }

              return (
                <div key={capability.id} className={`group space-y-4 ${getBorderClasses(capability.name)}`}>
                  <div className="flex items-start gap-4">
                  {capability.icon && (
                    <div className="flex-shrink-0 text-2xl mt-1">{capability.icon}</div>
                  )}
                  <div className="min-w-0 flex-1 space-y-3">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-foreground">
                          {capability.name}
                        </h3>
                        <div className="flex items-center gap-1.5">
                          <div className={getLevelDot(capability.level)} />
                          <span className="text-xs font-medium text-muted-foreground capitalize">
                            {capability.level}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {capability.description}
                      </p>
                    </div>
                    
                    {capability.examples.length > 0 && (
                      <div className="space-y-2">
                        {capability.examples.slice(0, 2).map((example, index) => (
                          <div key={index} className="flex items-start gap-3 text-sm text-muted-foreground">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary/50 mt-2 flex-shrink-0" />
                            <span>{example}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {capability.demoUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-primary hover:text-primary/80 hover:bg-primary/5 p-0"
                        onClick={() => window.open(capability.demoUrl, '_blank')}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Try Demo →
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Demo Videos */}
      {employee.demoVideos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" />
              Demo Videos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employee.demoVideos.map((video) => (
                <div
                  key={video.id}
                  className="group overflow-hidden rounded-xl border border-border/50 bg-card/50 transition-all hover:border-border hover:shadow-sm"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        className="bg-white/90 text-gray-900 hover:bg-white shadow-lg"
                        onClick={() => window.open(video.url, '_blank')}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Play
                      </Button>
                    </div>
                    <div className="absolute bottom-2 right-2 rounded bg-black/70 px-2 py-1 text-xs text-white">
                      {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-foreground mb-1">
                      {video.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {video.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Technical Specifications */}
      {employee.specifications.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Technical Specifications</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {employee.specifications.map((spec) => (
              <div
                key={spec.id}
                className="flex items-center justify-between rounded-lg border border-border/50 bg-card/30 px-4 py-3 transition-colors hover:bg-card/50"
              >
                <span className="text-sm font-medium text-foreground truncate">
                  {spec.name}
                </span>
                <span className="ml-2 font-mono text-sm text-muted-foreground">
                  {spec.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

CapabilityShowcase.displayName = "CapabilityShowcase"